import { Anchor, Button, Group, Loader, Modal, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { SUPPORT_EMAIL } from '../constants/supportContacts';
import useFetch from '../helpers/useFetch';
import ErrorMessage from './ErrorMessage';

// `startMs`/`endMs` are instants. The zone the CS team keeps its calendar in
// rides along in the response as `timeZone` but is not what we render in - the
// viewer sees their own zone, which is what a formatter built with no explicit
// timeZone uses.
const dayFormat = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
const timeFormat = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

// `Intl.DateTimeFormat.format` throws a RangeError on an Invalid Date rather
// than returning a placeholder, so anything built from a response's numbers
// has to be checked before it reaches a formatter.
const hasNumericInstant = (ms) => typeof ms === 'number' && Number.isFinite(ms);

// A day heading is recomputed from each instant rather than taken from the
// order the slots arrive in: the server groups them in its own zone, so two
// consecutive slots there can be on different local days here (and back).
const groupByLocalDay = (slots) => {
  const days = new Map();
  slots.forEach((slot) => {
    // An off-contract slot with no usable instant cannot be grouped or
    // rendered as a time - drop it rather than crash the whole picker on it.
    if (!hasNumericInstant(slot?.startMs)) {
      return;
    }
    const start = new Date(slot.startMs);
    const key = `${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;
    if (!days.has(key)) {
      days.set(key, { key, label: dayFormat.format(start), slots: [] });
    }
    days.get(key).slots.push(slot);
  });
  return [...days.values()];
};

const hostDescription = (mode, csm) =>
  mode === 'assigned' && csm ? `with your Customer Success Manager ${csm.firstName}` : 'with our Customer Success team';

// `outcome_unknown` means the booking may already exist, so the only safe move
// is to replay the identical request. Bounded, because replaying forever would
// leave the user staring at a spinner.
const MAX_OUTCOME_UNKNOWN_RETRIES = 3;

const SupportPrompt = ({ children }) => (
  <Text size="sm" c="dimmed">
    {children} <Anchor href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Anchor>.
  </Text>
);

// Mounted only while open, so every piece of state below - including the
// idempotency key - starts fresh on each open.
const OnboardingBookingModal = ({ onClose, onBooked, onUnresolved }) => {
  const fetch = useFetch();
  const { domain } = useSelector((state) => state.domain);

  const [availability, setAvailability] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [selectedStartMs, setSelectedStartMs] = useState(null);
  const [booking, setBooking] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  // Set once the outcome_unknown retry bound in `book` is exhausted. From
  // then on the picker is locked (see renderBody) rather than left live -
  // see the comment in `book` for why.
  const [retriesExhausted, setRetriesExhausted] = useState(false);
  // Minted once per modal open. Every retry of the same attempt must send this
  // exact value - a fresh key on a retry books the customer a second time - so
  // it is re-minted only where the current attempt is definitively over and the
  // user has to pick a different slot. That happens either immediately (the
  // `slot_taken` branch below) or later, when the user picks a slot other than
  // the one `lastAttemptStartMsRef` recorded for a still-open failed attempt
  // (see `selectSlot`).
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  // The startMs of the most recent book() attempt that did not resolve into a
  // fresh key on its own (i.e. a generic failure, or the outcome_unknown bound
  // running out) - null while no such attempt is pending a new pick.
  const lastAttemptStartMsRef = useRef(null);

  const onboardingUrl = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/onboarding`;

  useEffect(() => {
    let stale = false;
    fetch(`${onboardingUrl}/slots`)
      .then((result) => {
        if (!stale) {
          setAvailability(result);
        }
      })
      .catch((err) => {
        if (!stale) {
          setLoadError(err?.message || 'We could not load the available times.');
        }
      });
    return () => {
      stale = true;
    };
  }, [fetch, onboardingUrl]);

  const book = useCallback(async () => {
    setBooking(true);
    setBookingError(null);
    // This attempt is now "in flight" for `selectedStartMs`. If it ends in a
    // failure that leaves the key in place (anything below other than
    // `slot_taken`), a later pick of a *different* slot must mint a fresh key
    // - see `selectSlot`.
    lastAttemptStartMsRef.current = selectedStartMs;
    for (let attempt = 0; attempt <= MAX_OUTCOME_UNKNOWN_RETRIES; attempt += 1) {
      try {
        const result = await fetch(`${onboardingUrl}/book`, {
          method: 'POST',
          body: JSON.stringify({ startMs: selectedStartMs, idempotencyKey }),
        });
        // The 409 bodies this same loop keys off (`err.booked`) imply the
        // field exists on the success path too - an explicit `false` there is
        // a deferred/queued booking, not a confirmed one, and must not render
        // "You are booked" (which flips OnboardingBookingButton.jsx to
        // "Contact Support").
        if (result?.booked === false) {
          setBookingError(result?.message || 'We could not book that time. Please try again.');
          setBooking(false);
          return;
        }
        setConfirmation(result);
        setBooking(false);
        // `onBooked` is not fired here. It flips a local `justBooked` flag in
        // OnboardingBookingButton.jsx, which switches that component to a
        // branch that never renders this modal - firing it immediately would
        // unmount the modal the instant the POST resolves, before the user
        // ever sees the confirmation body below. It fires instead from
        // `handleClose`, once the user actually dismisses that confirmation.
        return;
      } catch (err) {
        if (err?.reason === 'slot_taken') {
          setAvailability((current) => ({ ...current, slots: err.slots ?? [] }));
          setSelectedStartMs(null);
          setIdempotencyKey(crypto.randomUUID());
          lastAttemptStartMsRef.current = null;
          setBookingError('Someone else just took that time. Please pick another one.');
          setBooking(false);
          return;
        }
        if (err?.reason !== 'outcome_unknown') {
          setBookingError(err?.message || 'We could not book that time. Please try again.');
          setBooking(false);
          return;
        }
      }
    }
    setBookingError(
      'We could not confirm whether your session was booked. Please reload the page to see where it stands.'
    );
    setBooking(false);
    // The ambiguity here never resolves client-side: we cannot tell whether
    // one of the outcome_unknown attempts actually committed. Locking the
    // picker (see renderBody) rather than leaving it live is what stops the
    // user from picking a different slot, getting a fresh key by that path,
    // and double-booking if the earlier attempt did in fact land.
    setRetriesExhausted(true);
  }, [fetch, idempotencyKey, onboardingUrl, selectedStartMs]);

  const selectSlot = (startMs) => {
    // A prior attempt failed (generic error, or the outcome_unknown bound ran
    // out) without minting a fresh key, and the user is now picking a
    // different slot than that attempt sent - this is a new booking attempt
    // by the brief's rule, so it gets its own key. Re-picking the *same* slot
    // is a manual retry of the same attempt and keeps the key.
    if (lastAttemptStartMsRef.current !== null && startMs !== lastAttemptStartMsRef.current) {
      setIdempotencyKey(crypto.randomUUID());
      lastAttemptStartMsRef.current = null;
    }
    setSelectedStartMs(startMs);
    setBookingError(null);
  };

  // Fires `onBooked` - which flips OnboardingBookingButton's local
  // `justBooked` state and, as a result, unmounts this modal - only as part
  // of the user actually dismissing it. That is what lets the confirmation
  // body stay on screen for as long as the user is looking at it, however
  // they close the modal (the Done button below, the header's close button,
  // which stays live throughout, or Escape/overlay-click once those re-enable
  // when `booking` goes back to false).
  //
  // `retriesExhausted` gets the same treatment for the opposite reason: once
  // the picker is locked (see renderBody), `booking` is back to `false`, so
  // Escape/overlay-click/the header X are all live again, and closing here is
  // exactly what a user reading "reload the page" will do instead. Without
  // `onUnresolved`, closing just calls `onClose()` and
  // OnboardingBookingButton.jsx - never told the outcome is ambiguous - still
  // renders "Book Onboarding Session", whose one click remounts this
  // component with a fresh idempotency key and a live picker, defeating the
  // lock this state exists to enforce. Firing `onUnresolved` instead lets
  // OnboardingBookingButton.jsx hold that ambiguity in its own state and swap
  // the button for a "reload to check your booking" affordance that cannot
  // reopen the picker.
  //
  // `booking` gets the identical treatment for the identical reason: the
  // header close button is deliberately left live while a POST is still in
  // flight (see the Modal props below and the in-body note shown while
  // `booking` is true), and closing then is the same "we can't tell whether
  // this landed" state as an exhausted retry - `confirmation` is `null` and
  // `retriesExhausted` is still `false` because the request never resolved
  // long enough to hit that bound. Without checking `booking` here too,
  // OnboardingBookingButton.jsx would keep offering "Book Onboarding
  // Session"; one click remounts this component with a fresh idempotency key
  // and a live picker, and picking a *different* slot than the still-pending
  // attempt then double-books, since neither the new key nor a `slot_taken`
  // 409 can dedupe against a write this component never learned the outcome
  // of.
  const handleClose = () => {
    if (confirmation) {
      onBooked?.();
    } else if (retriesExhausted || booking) {
      onUnresolved?.();
    }
    onClose();
  };

  const renderBody = () => {
    if (loadError) {
      return (
        <Stack gap="xs">
          <ErrorMessage message={loadError} />
          <SupportPrompt>Please try again, or reach us at</SupportPrompt>
        </Stack>
      );
    }

    if (!availability) {
      return (
        <Group justify="center" py="xl">
          <Loader size="sm" />
          <Text size="sm">Loading available times…</Text>
        </Group>
      );
    }

    if (confirmation) {
      // useFetch normalises some successes to `{}` (a 202/204, or a 200 whose
      // body fails to parse - see useFetch.jsx), which is a truthy
      // `confirmation` with no numeric `startMs`. Render the same "booked"
      // state without a time rather than crash the whole app on
      // `new Date(undefined)` reaching the formatters above.
      const hasStart = hasNumericInstant(confirmation.startMs);
      const start = hasStart ? new Date(confirmation.startMs) : null;
      const withCsm = confirmation.csm ? ` with ${confirmation.csm.firstName} ${confirmation.csm.lastName}` : '';
      return (
        <Stack gap="xs">
          <Text>
            {hasStart ? (
              <>
                You are booked for {dayFormat.format(start)} at {timeFormat.format(start)}
                {withCsm}.
              </>
            ) : (
              <>You are booked{withCsm}. Check your calendar invite for the exact time.</>
            )}
          </Text>
          {confirmation.meetLink ? (
            <Text size="sm">
              Join here:{' '}
              <Anchor href={confirmation.meetLink} target="_blank" rel="noopener noreferrer">
                {confirmation.meetLink}
              </Anchor>
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              The video call link will be in your calendar invite.
            </Text>
          )}
          <Group justify="flex-end">
            <Button onClick={handleClose}>Done</Button>
          </Group>
        </Stack>
      );
    }

    if (retriesExhausted) {
      return (
        <Stack gap="xs">
          <ErrorMessage message={bookingError} />
          <SupportPrompt>If it does not show up after reloading, write to</SupportPrompt>
        </Stack>
      );
    }

    const slots = availability.slots ?? [];
    const days = groupByLocalDay(slots);
    // All slots come from the same booking type, so the first one's length
    // stands in for the session's duration; there just isn't one to show when
    // the list is empty. Read off `days[0]`, not the raw `slots[0]` - an
    // off-contract slot with no numeric `startMs` is dropped by
    // `groupByLocalDay` but would still be `slots[0]` here, and subtracting
    // through a missing instant is `NaN` (falsy), silently dropping the hint
    // for a reason unrelated to the grid the user actually sees.
    const firstSlot = days[0]?.slots[0];
    const durationMinutes = firstSlot ? Math.round((firstSlot.endMs - firstSlot.startMs) / 60000) : null;

    return (
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Pick a time {hostDescription(availability.mode, availability.csm)}
          {durationMinutes ? ` (${durationMinutes} minutes)` : ''}. Times are shown in your own time zone.
        </Text>
        {bookingError && <ErrorMessage message={bookingError} />}
        {booking && (
          // `useFetch` sets no timeout or abort signal, so this request can
          // hang far longer than the spinner suggests. The header close
          // button stays reachable throughout (see the Modal props below)
          // specifically so a stalled request cannot lock the user in - this
          // just explains what closing now does and does not do.
          <Text size="xs" c="dimmed">
            You can close this modal while it is booking, but that will not cancel the attempt - check your calendar to
            see whether it went through.
          </Text>
        )}
        {days.length === 0 ? (
          <SupportPrompt>There are no times available right now. To arrange one, write to</SupportPrompt>
        ) : (
          <>
            {days.map((day) => (
              <Stack key={day.key} gap="xs">
                <Title order={6}>{day.label}</Title>
                <SimpleGrid cols={{ base: 2, xs: 3, sm: 4 }} spacing="xs">
                  {day.slots.map((slot) => (
                    <Button
                      key={slot.startMs}
                      variant={slot.startMs === selectedStartMs ? 'filled' : 'default'}
                      onClick={() => selectSlot(slot.startMs)}
                      disabled={booking}
                    >
                      {timeFormat.format(new Date(slot.startMs))}
                    </Button>
                  ))}
                </SimpleGrid>
              </Stack>
            ))}
            <Group justify="flex-end">
              <Button onClick={book} disabled={selectedStartMs === null} loading={booking}>
                Confirm booking
              </Button>
            </Group>
          </>
        )}
      </Stack>
    );
  };

  return (
    <Modal
      opened
      onClose={handleClose}
      title="Book your onboarding session"
      size="lg"
      // A stray Escape or an overlay click mid-POST would tear the modal down
      // while a booking is in flight - `setConfirmation` afterwards is then a
      // no-op on an unmounted component, so the user never sees what they just
      // booked and is likely to open the modal again and pick a second time.
      // That risk is worth guarding against for the *accidental* dismissals,
      // but not at the cost of the deliberate one: `useFetch` sets no
      // timeout or abort signal (see useFetch.jsx), so a stalled connection
      // can leave `booking` true for minutes. `withCloseButton` therefore
      // stays live throughout - see the in-body note shown while `booking`
      // is true for what closing then does and does not do - so a hung
      // request can never leave the modal, and the whole app behind it,
      // impossible to dismiss.
      closeOnEscape={!booking}
      closeOnClickOutside={!booking}
      withCloseButton
      closeButtonProps={{ 'aria-label': 'Close' }}
    >
      {renderBody()}
    </Modal>
  );
};

export default OnboardingBookingModal;
